/**
 * Money / precision utilities (frontend).
 *
 * Prices are stored and transmitted as INTEGER cents. The UI lets users type
 * euro amounts, which must be converted to cents without accumulating IEEE-754
 * rounding error.
 *
 * The naive `Math.round(parseFloat(value) * 100)` is usually fine, but float
 * representation means values such as `1.005 * 100 === 100.49999999999999`,
 * which rounds DOWN to 100 instead of the expected 101. To guard against this
 * we snap the intermediate product to 4 decimal places (sub-cent precision)
 * before the final whole-cent round.
 */

/** Decimal places retained for intermediate (sub-cent) precision. */
export const INTERMEDIATE_DECIMALS = 4;

const INTERMEDIATE_SCALE = 10 ** INTERMEDIATE_DECIMALS;

/**
 * Snap a number to {@link INTERMEDIATE_DECIMALS} decimal places, discarding
 * floating-point noise while keeping sub-cent precision.
 */
export function toIntermediatePrecision(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.round(value * INTERMEDIATE_SCALE) / INTERMEDIATE_SCALE;
}

/**
 * Normalize a typed euro string so `parseFloat` accepts both `.` and `,`.
 * Does not strip grouping spaces — callers should not feed those in.
 */
export function normalizeEuroInput(value: string): string {
  return value.trim().replace(',', '.');
}

/**
 * Convert a euro amount (number or numeric string) to integer cents.
 *
 * Maintains 4-decimal intermediate precision so half-cent boundaries round
 * correctly. Returns `null` when the input cannot be parsed as a finite number,
 * letting callers preserve the previous value instead of writing NaN.
 */
export function eurosToCents(value: number | string): number | null {
  const euros = typeof value === 'string' ? parseFloat(normalizeEuroInput(value)) : value;
  if (euros == null || !Number.isFinite(euros)) return null;
  // Snap the cents product to intermediate precision before final rounding.
  const cents = toIntermediatePrecision(euros * 100);
  return Math.round(cents);
}

/** Convert integer cents to a euro amount (number). */
export function centsToEuros(cents: number): number {
  if (!Number.isFinite(cents)) return 0;
  return toIntermediatePrecision(cents / 100);
}

/** Display-only euro string with two decimals (e.g. `19.99`). */
export function formatCentsAsEuros(cents: number): string {
  return centsToEuros(cents).toFixed(2);
}

/**
 * Money as a Finnish reader expects it: `12,50 €` (#2724).
 *
 * `formatCentsAsEuros` above returns `12.50` — no comma, no symbol — so every
 * screen that shows a price wrapped it by hand. Thirteen components ended up
 * with their own `formatPrice`/`formatEuro`, in five spellings. They all
 * produce the same string (verified), so this is duplication rather than a
 * live bug, but it is the kind that turns into one the first time someone
 * changes a single copy.
 *
 * Two details worth having in one place:
 *
 * - **Non-breaking space before €.** Finnish typography puts a space between
 *   the number and the unit, and a normal space lets the symbol wrap onto
 *   its own line at the end of a table cell.
 * - **No `-0,00 €`.** A sub-cent negative rounds to zero, and a minus in
 *   front of a zero reads as an error. `Math.round(-0.4)` gives `-0`, and
 *   `-0 < 0` is false in JS, so rounding first is what prevents the sign —
 *   no extra branch needed, but the ordering matters.
 */
export function formatCentsForDisplay(cents: number): string {
  if (!Number.isFinite(cents)) return '0,00\u00A0€';
  const rounded = Math.round(cents);
  const sign = rounded < 0 ? '-' : '';
  const body = (Math.abs(rounded) / 100).toFixed(2).replace('.', ',');
  return `${sign}${body}\u00A0€`;
}

/**
 * Line total in integer cents: a gross (VAT-included) unit price × quantity,
 * rounded once.
 *
 * Computed directly from the entered gross cents — NOT derived via a
 * gross -> net -> gross round trip, which is lossy at the whole-cent
 * boundary and could silently change what the user typed (#1022).
 */
export function lineTotalGrossCents(grossCents: number, quantity: number): number {
  if (!Number.isFinite(grossCents) || !Number.isFinite(quantity)) return 0;
  return Math.round(toIntermediatePrecision(grossCents * quantity));
}

/**
 * Whole-cent boundary for invoice money totals. Mirrors shared `toWholeCents`
 * and must not go through a `roundCents` warning — that warning is how the
 * backend detects premature rounding of a 4 dp net before VAT.
 */
function toWholeCents(value: number): number {
  return Math.round(toIntermediatePrecision(value));
}

/**
 * Line net after the line's own discount, kept at 4 decimal places. VAT must
 * be derived from this (or a sum of these), never from the whole-cent snapshot.
 */
export function lineNetAfterDiscountPrecise(
  unitPriceCents: number,
  quantity: number,
  discountPercent: number,
): number {
  if (!Number.isFinite(unitPriceCents) || !Number.isFinite(quantity)) return 0;
  const rate = Number.isFinite(discountPercent) ? discountPercent : 0;
  const grossQty = toIntermediatePrecision(unitPriceCents * quantity);
  const discount = percentOfCents(grossQty, rate);
  return toIntermediatePrecision(grossQty - discount);
}

/**
 * Whole-cent line net for `line_total_cents` storage. Do not feed this into VAT.
 */
export function lineNetAfterDiscountCents(
  unitPriceCents: number,
  quantity: number,
  discountPercent: number,
): number {
  return toWholeCents(lineNetAfterDiscountPrecise(unitPriceCents, quantity, discountPercent));
}

/**
 * 4 dp hourly net price for a legacy work-order labor row billed in hours
 * (`labor_cost_cents` is the line net, `estimated_hours` its quantity), or
 * `null` when no 4 dp price reproduces that net exactly. Mirrors staff-api
 * `legacyLaborHourlyUnitPriceCents` (#2704): such rows stay one lump-sum piece.
 */
export function legacyLaborHourlyUnitPriceCents(
  laborCostCents: number,
  hours: number,
): number | null {
  if (!Number.isFinite(laborCostCents) || laborCostCents <= 0) return null;
  if (!Number.isFinite(hours) || hours <= 0) return null;
  const targetNet = lineNetAfterDiscountPrecise(laborCostCents, 1, 0);
  const nearest = toIntermediatePrecision(laborCostCents / hours);
  const step = 1 / INTERMEDIATE_SCALE;
  for (const candidate of [nearest, nearest - step, nearest + step]) {
    const unitPriceCents = toIntermediatePrecision(candidate);
    if (lineNetAfterDiscountPrecise(unitPriceCents, hours, 0) === targetNet) {
      return unitPriceCents;
    }
  }
  return null;
}

/**
 * Description of a legacy labor row billed as one lump-sum piece although it
 * carries hours (#2704), mirroring staff-api: the hours go into the text, e.g. "Jarruhuolto (3 h)",
 * so the customer still sees them while the total stays exact. Hours print
 * with a Finnish decimal comma and at most two decimals.
 */
export function legacyLaborPieceDescription(description: string, hours: number): string {
  const text = (Math.round(hours * 100) / 100).toString().replace('.', ',');
  const trimmed = description.trim();
  return trimmed.length > 0 ? `${trimmed} (${text} h)` : `(${text} h)`;
}

export interface InvoiceCalcLine {
  unitPriceCents: number;
  quantity: number;
  discountPercent: number;
  taxable: boolean;
  taxPercent: number;
}

export interface TaxBreakdownEntry {
  taxPercent: number;
  netCents: number;
  taxCents: number;
  grossCents: number;
}

export interface InvoiceFromLinesResult {
  subtotalCents: number;
  discountCents: number;
  taxCents: number;
  totalCents: number;
  taxBreakdown: TaxBreakdownEntry[];
  lineNetCents: number[];
  lineGrossCents: number[];
}

function allocateProportional(total: number, weights: number[]): number[] {
  const weightSum = weights.reduce((a, b) => a + b, 0);
  if (total <= 0 || weightSum <= 0) return weights.map(() => 0);

  const exact = weights.map((w) => (total * w) / weightSum);
  const floors = exact.map((v) => Math.floor(v));
  let remainder = total - floors.reduce((a, b) => a + b, 0);
  const order = exact
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac);
  const result = [...floors];
  for (let k = 0; k < order.length && remainder > 0; k++) {
    const entry = order[k];
    if (!entry) continue;
    result[entry.i] = (result[entry.i] ?? 0) + 1;
    remainder--;
  }
  return result;
}

/**
 * Invoice money math — same basis as `@vehiclespace/shared` `calculateInvoiceFromLines`.
 * VAT is taken from 4 dp line net, and each rate group's rounded gross is split
 * back onto lines so Σ `lineGrossCents` === `totalCents`.
 */
export function calculateInvoiceFromLines(
  lines: InvoiceCalcLine[],
  invoiceDiscountCents = 0,
): InvoiceFromLinesResult {
  const lineNetPrecise = lines.map((line) =>
    lineNetAfterDiscountPrecise(line.unitPriceCents, line.quantity, line.discountPercent),
  );

  const subtotalCents = toWholeCents(lineNetPrecise.reduce((a, b) => a + b, 0));
  const lineNetCents = allocateProportional(subtotalCents, lineNetPrecise);
  const discountCents = Math.max(0, Math.min(toWholeCents(invoiceDiscountCents), subtotalCents));

  const groups = new Map<number, { rate: number; netPrecise: number; lineIdx: number[] }>();
  lineNetPrecise.forEach((net, i) => {
    const line = lines[i];
    if (!line) return;
    const rate = line.taxable ? line.taxPercent : 0;
    const group = groups.get(rate) ?? { rate, netPrecise: 0, lineIdx: [] };
    group.netPrecise = toIntermediatePrecision(group.netPrecise + net);
    group.lineIdx.push(i);
    groups.set(rate, group);
  });

  const groupList = [...groups.values()];
  const discountAlloc = allocateProportional(
    discountCents,
    groupList.map((g) => g.netPrecise),
  );
  const groupNetAfterPrecise = groupList.map((group, gi) =>
    toIntermediatePrecision(group.netPrecise - (discountAlloc[gi] ?? 0)),
  );

  const taxableCents = subtotalCents - discountCents;
  const groupNetCents = allocateProportional(taxableCents, groupNetAfterPrecise);

  const taxBreakdownUnsorted: TaxBreakdownEntry[] = groupList.map((group, gi) => {
    const netAfterPrecise = groupNetAfterPrecise[gi] ?? 0;
    const netCents = groupNetCents[gi] ?? 0;
    const taxCents = group.rate > 0 ? toWholeCents(percentOfCents(netAfterPrecise, group.rate)) : 0;
    return {
      taxPercent: group.rate,
      netCents,
      taxCents,
      grossCents: netCents + taxCents,
    };
  });
  const taxBreakdown = [...taxBreakdownUnsorted].sort((a, b) => b.taxPercent - a.taxPercent);

  const taxCents = taxBreakdown.reduce((a, g) => a + g.taxCents, 0);
  const totalCents = subtotalCents - discountCents + taxCents;

  const lineGrossCents = lines.map(() => 0);
  groupList.forEach((group, gi) => {
    const groupGross = taxBreakdownUnsorted[gi]?.grossCents ?? 0;
    const weights = group.lineIdx.map((idx) => lineNetPrecise[idx] ?? 0);
    let allocated = allocateProportional(groupGross, weights);
    const allocatedSum = allocated.reduce((a, b) => a + b, 0);
    if (groupGross > 0 && allocatedSum === 0 && group.lineIdx.length > 0) {
      allocated = group.lineIdx.map((_, i) => (i === 0 ? groupGross : 0));
    }
    group.lineIdx.forEach((lineIdx, k) => {
      lineGrossCents[lineIdx] = allocated[k] ?? 0;
    });
  });

  return { subtotalCents, discountCents, taxCents, totalCents, taxBreakdown, lineNetCents, lineGrossCents };
}

/** Keep digits and a single decimal separator so Finnish commas survive typing. */
export function sanitizeDecimalInput(value: string): string {
  const cleaned = value.replace(/[^0-9.,]/g, '');
  const separator = cleaned.includes(',') ? ',' : cleaned.includes('.') ? '.' : null;
  if (!separator) return cleaned;
  const first = cleaned.indexOf(separator);
  const head = cleaned.slice(0, first + 1);
  const tail = cleaned.slice(first + 1).replace(/[.,]/g, '');
  return `${head}${tail}`;
}

/**
 * Integer cents for hours × hourly rate, rounded once at the end (#1438/#1439).
 * Returns 0 when the rate or hours are missing/non-positive so callers can
 * hide a €0 figure instead of showing "0,00 €".
 */
export function laborCentsFromHours(rateCents: number, hours: number): number {
  if (!Number.isFinite(rateCents) || !Number.isFinite(hours) || rateCents <= 0 || hours <= 0) {
    return 0;
  }
  return Math.round(toIntermediatePrecision(rateCents * hours));
}

/**
 * `percent` percent of a cents amount with the same 4-decimal snap as
 * staff-api `percentOfCents`. Display-side only.
 */
export function percentOfCents(amountCents: number, percent: number): number {
  return toIntermediatePrecision((amountCents * percent) / 100);
}

/**
 * Tax-exclusive net cents from a gross (VAT-included) amount. Mirrors staff-api
 * `grossToNetCents` — the single documented rounding rule for this direction
 * (docs/VAT-BASIS.md § 4). Used once, when a typed gross price is submitted.
 */
export function grossToNetCents(grossCents: number, vatPercent: number): number {
  if (!Number.isFinite(grossCents)) return 0;
  if (!Number.isFinite(vatPercent) || vatPercent <= 0) {
    return toIntermediatePrecision(grossCents);
  }
  return toIntermediatePrecision((grossCents * 100) / (100 + vatPercent));
}

/**
 * Display-only gross cents for one invoice line's stored net amount.
 *
 * Every price a user sees is VAT-inclusive (docs/VAT-BASIS.md § 1). A
 * non-taxable line accrues no VAT, so its gross equals its net — it must not be
 * pushed through the fallback rate.
 */
export function displayGrossCents(
  netCents: number,
  taxable: boolean,
  taxPercent: number | null | undefined,
  fallbackTaxPercent = 0,
): number {
  if (!taxable) return Math.round(toIntermediatePrecision(netCents));
  return netToGrossCents(netCents, taxPercent ?? fallbackTaxPercent);
}

export interface DisplayLine {
  unitPriceCents: number;
  quantity: number;
  discountPercent?: number | null;
  lineTotalCents: number;
  taxable: boolean;
  taxPercent: number | null | undefined;
}

/**
 * Display-only gross cents for one document line's total (#3231). Mirrors shared `displayLineGrossCents`.
 *
 * The unit price is a 4 dp net price while `lineTotalCents` is its whole-cent
 * snapshot, so converting the two to gross separately can round a cent apart
 * and a one-unit line reads "1 × 55,00 € = 55,01 €". The line's gross is
 * therefore derived from the same precise net the unit price comes from
 * (unit × quantity after the line discount) and rounded once. A line whose
 * stored total is not its unit × quantity (beyond the one cent the invoice
 * net allocation may shift) — a fixed-price service with a blended display
 * rate, a manually edited total — keeps its stored money.
 */
export function displayLineGrossCents(line: DisplayLine, fallbackTaxPercent = 0): number {
  const precise = lineNetAfterDiscountPrecise(line.unitPriceCents, line.quantity, line.discountPercent ?? 0);
  // Stored line nets are allocated so they sum to the invoice net exactly,
  // which can move a line one cent off its own unit × quantity; that jitter
  // must not win over the price the customer reads.
  const net = Math.abs(toWholeCents(precise) - line.lineTotalCents) <= 1 ? precise : line.lineTotalCents;
  return displayGrossCents(net, line.taxable, line.taxPercent, fallbackTaxPercent);
}

/**
 * Display-only: VAT-inclusive cents from stored net. Do not persist this
 * back as a typed price (#1708).
 */
export function netToGrossCents(netCents: number, vatPercent: number): number {
  if (!Number.isFinite(netCents)) return 0;
  if (!Number.isFinite(vatPercent) || vatPercent <= 0) {
    return Math.round(toIntermediatePrecision(netCents));
  }
  return Math.round(toIntermediatePrecision(netCents)) + Math.round(percentOfCents(netCents, vatPercent));
}
